import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import AnimatedSection from '../components/AnimatedSection';

/* ---- Skill Bar Component ---- */
const SkillBar = ({ skill, percent, color = '#ff6b35', delay = 0 }) => {
  const [width, setWidth] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setTimeout(() => setWidth(percent), delay);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [percent, delay]);

  return (
    <div ref={ref} style={{ marginBottom: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
        <span style={{ fontWeight: 600, color: '#1a1a5e', fontSize: '0.9rem' }}>{skill}</span>
        <span style={{ fontWeight: 700, color: color, fontSize: '0.9rem' }}>{percent}%</span>
      </div>
      <div style={{
        height: '10px', background: '#e9ecef', borderRadius: '50px', overflow: 'hidden'
      }}>
        <div style={{
          height: '100%',
          width: `${width}%`,
          background: `linear-gradient(90deg, #1a1a5e, ${color})`,
          borderRadius: '50px',
          transition: `width 1.5s cubic-bezier(0.4, 0, 0.2, 1) ${delay}ms`,
          position: 'relative',
        }}>
          <div style={{
            position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)',
            width: '14px', height: '14px', background: color, borderRadius: '50%',
            border: '2px solid white', boxShadow: `0 0 8px ${color}`,
          }}></div>
        </div>
      </div>
    </div>
  );
};

const features = [
  {
    icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93A10 10 0 1 1 4.93 19.07 10 10 0 0 1 19.07 4.93"/></svg>,
    title: 'SERVICES', desc: 'Full-stack web development, mobile apps, digital marketing and SEO services tailored for your business needs.'
  },
  {
    icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>,
    title: 'Explore Industries', desc: 'We serve diverse industries including healthcare, education, real estate, e-commerce, hospitality and more.'
  },
  {
    icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>,
    title: 'Our Platforms', desc: 'We build on modern platforms: React, Node.js, WordPress, PHP, and deploy on secure cloud infrastructure.'
  },
  {
    icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>,
    title: 'Navigate Your Next', desc: 'Let us guide your digital transformation journey with innovative strategies and cutting-edge technologies.'
  },
];

const skills = [
  { skill: 'HTML', percent: 100, color: '#e34c26' },
  { skill: 'CSS', percent: 90, color: '#264de4' },
  { skill: 'JavaScript', percent: 95, color: '#f7df1e' },
  { skill: 'PHP', percent: 92, color: '#777bb3' },
  { skill: 'WordPress / CMS', percent: 90, color: '#21759b' },
  { skill: 'Photoshop', percent: 88, color: '#31a8ff' },
];

const clientsData = [
  { name: 'GLOBAL',              sub: 'Speciality Hospital',    tag: 'Healthcare',  color: '#e74c3c', accent: '#c0392b', desc: 'NABL Accredited Hospital, Trusted Care',    icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg> },
  { name: 'Sakhi',               sub: 'Welfare Society',        tag: 'NGO',         color: '#e91e63', accent: '#c2185b', desc: 'Empowering Women & Communities',            icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg> },
  { name: 'MEKAGRAVURE',         sub: 'Méka Studios',           tag: 'Creative',    color: '#2c3e50', accent: '#34495e', desc: 'Premium Engraving & Print Solutions',       icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg> },
  { name: 'CA Associates',       sub: 'Chartered Finance',      tag: 'Finance',     color: '#7f8c8d', accent: '#636e72', desc: 'Certified Accounting & Tax Services',      icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg> },
  { name: 'THE PLANNING\nPARADISE', sub: 'Architecture & Design', tag: 'Real Estate', color: '#1a1a1a', accent: '#2d3436', desc: 'Innovative Architectural Planning',       icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8"><polygon points="3 9 12 2 21 9 21 20 3 20 3 9"/></svg> },
  { name: 'WELL HOME',           sub: 'Media Studios',          tag: 'Media',       color: '#1a1a5e', accent: '#0a0a2e', desc: 'Creative Media & Film Production',          icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg> },
  { name: 'Wellcraft',           sub: 'Digital Solutions',      tag: 'Technology',  color: '#2980b9', accent: '#1a6fa3', desc: 'Precision Digital Craftsmanship',           icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93A10 10 0 1 1 4.93 19.07 10 10 0 0 1 19.07 4.93"/></svg> },
  { name: 'RMS',                 sub: 'Rashtriya Military School', tag: 'Education', color: '#27ae60', accent: '#1e8449', desc: 'Excellence in Military Education',          icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg> },
  { name: 'Atal Bal Palak',      sub: 'Child Welfare',          tag: 'Social',      color: '#8bc34a', accent: '#6aaa30', desc: 'PARVARISH SUNHARE KAL KI',                  icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
  { name: 'AB PRO',              sub: 'Construction',           tag: 'Real Estate', color: '#607d8b', accent: '#4a6572', desc: 'Best Build Quality',                          icon: <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg> },
];

const aboutSlides = [
  {
    img: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=900&q=80',
    label: 'Web Development',
    overlay: 'linear-gradient(to top, rgba(26,26,94,0.82) 0%, rgba(26,26,94,0.2) 60%, transparent 100%)',
  },
  {
    img: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=900&q=80',
    label: 'UI / UX Design',
    overlay: 'linear-gradient(to top, rgba(255,107,53,0.75) 0%, rgba(255,107,53,0.15) 60%, transparent 100%)',
  },
  {
    img: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=900&q=80',
    label: 'Digital Marketing',
    overlay: 'linear-gradient(to top, rgba(10,10,46,0.85) 0%, rgba(10,10,46,0.2) 60%, transparent 100%)',
  },
  {
    img: 'https://images.unsplash.com/photo-1531403009284-440f080d1e12?w=900&q=80',
    label: 'App Development',
    overlay: 'linear-gradient(to top, rgba(155,89,182,0.75) 0%, rgba(155,89,182,0.15) 60%, transparent 100%)',
  },
];

const About = () => {
  const [slideIdx, setSlideIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setSlideIdx(i => (i + 1) % aboutSlides.length), 3500);
    return () => clearInterval(t);
  }, []);

  return (
    <div>
      <style>{`
        .about-hero { padding: 150px 0 80px; background: linear-gradient(135deg, #050516, #0a0a2e, #1a1a5e); text-align: center; position: relative; overflow: hidden; }
        .features-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 28px; }
        .feature-card {
          background: white; border-radius: 16px; padding: 34px 28px;
          border: 1px solid #e9ecef; transition: all 0.4s ease;
          display: flex; gap: 20px; align-items: flex-start;
        }
        .feature-card:hover { transform: translateY(-6px); box-shadow: 0 15px 40px rgba(26,26,94,0.15); border-color: transparent; }
        .feature-icon {
          width: 58px; height: 58px; background: linear-gradient(135deg, #f0f2ff, #e8eaff);
          border-radius: 14px; display: flex; align-items: center; justify-content: center;
          font-size: 1.6rem; flex-shrink: 0; transition: all 0.4s ease;
        }
        .feature-card:hover .feature-icon { background: linear-gradient(135deg, #1a1a5e, #2d2d8e); }
        .feature-card h3 { font-size: 1rem; font-weight: 700; color: #1a1a5e; margin-bottom: 8px; }
        .feature-card p { font-size: 0.88rem; color: #6c757d; line-height: 1.7; }
        .about-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 60px; align-items: center; }

        /* ===== IMAGE SLIDER WRAPPER ===== */
        .about-img-wrapper { position: relative; padding: 20px 20px 50px 0; }

        /* outer frame with neon ring */
        .about-slider-frame {
          border-radius: 24px;
          overflow: hidden;
          position: relative;
          height: 400px;
          box-shadow: 0 30px 70px rgba(10,10,46,0.22);
        }

        /* each slide */
        .about-slide {
          position: absolute; inset: 0;
          opacity: 0;
          transition: opacity 0.9s cubic-bezier(0.4,0,0.2,1),
                      transform 0.9s cubic-bezier(0.4,0,0.2,1);
          transform: scale(1.04);
        }
        .about-slide.active {
          opacity: 1;
          transform: scale(1);
          z-index: 1;
        }
        .about-slide img {
          width: 100%; height: 100%;
          object-fit: cover; display: block;
          animation: slowZoomAbout 12s ease-in-out infinite alternate;
        }
        @keyframes slowZoomAbout {
          from { transform: scale(1); }
          to   { transform: scale(1.07); }
        }

        /* gradient overlay on each slide */
        .about-slide-overlay {
          position: absolute; inset: 0;
          background: linear-gradient(
            to top,
            rgba(10,10,46,0.75) 0%,
            rgba(26,26,94,0.35) 50%,
            transparent 100%
          );
        }

        /* label badge at bottom of slide */
        .about-slide-label {
          position: absolute; bottom: 20px; left: 20px; z-index: 2;
          background: rgba(255,255,255,0.12);
          border: 1px solid rgba(255,255,255,0.25);
          backdrop-filter: blur(10px);
          border-radius: 50px;
          padding: 8px 18px;
          color: white; font-size: 0.82rem; font-weight: 700;
          border-left: 3px solid #ff6b35;
          display: flex; align-items: center; gap: 8px;
        }

        /* animated neon ring around frame */
        .about-ring {
          position: absolute; inset: -4px;
          border-radius: 28px;
          border: 2px solid transparent;
          background: linear-gradient(white,white) padding-box,
                      linear-gradient(135deg,#00d4ff,#ff6b35,#a78bfa,#00d4ff) border-box;
          pointer-events: none;
          animation: ringRotate 6s linear infinite;
        }
        @keyframes ringRotate {
          0%   { filter: hue-rotate(0deg);   }
          100% { filter: hue-rotate(360deg); }
        }

        /* dot indicators */
        .about-dots {
          position: absolute; bottom: -36px; left: 50%;
          transform: translateX(-50%);
          display: flex; gap: 8px; z-index: 3;
        }
        .about-dot {
          width: 8px; height: 8px; border-radius: 50%;
          background: #dee2e6; cursor: pointer;
          transition: all 0.3s ease;
        }
        .about-dot.active {
          background: #ff6b35; width: 24px; border-radius: 4px;
        }

        /* prev / next arrows */
        .about-arrow {
          position: absolute; top: 50%; transform: translateY(-50%);
          z-index: 4;
          width: 36px; height: 36px; border-radius: 50%;
          background: rgba(255,255,255,0.18);
          border: 1px solid rgba(255,255,255,0.3);
          backdrop-filter: blur(8px);
          color: white; font-size: 1rem;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.3s ease;
        }
        .about-arrow:hover { background: #ff6b35; border-color: #ff6b35; }
        .about-arrow-l { left: 12px; }
        .about-arrow-r { right: 12px; }

        /* floating dot particles */
        .collage-particle {
          position: absolute; border-radius: 50%; pointer-events: none;
        }
        .cp1 { width:12px;height:12px; background:#00d4ff; top:-14px; left:30%;
                box-shadow:0 0 12px #00d4ff; animation:float 3s ease-in-out infinite; }
        .cp2 { width:8px; height:8px;  background:#ff6b35; bottom:-10px; right:25%;
                box-shadow:0 0 10px #ff6b35; animation:floatReverse 4s ease-in-out infinite 0.5s; }
        .cp3 { width:10px;height:10px; background:#a78bfa; top:40%; right:-14px;
                box-shadow:0 0 10px #a78bfa; animation:float 3.5s ease-in-out infinite 1s; }
        .cp4 { width:6px; height:6px;  background:#ff00aa; bottom:30%; left:-12px;
                box-shadow:0 0 8px #ff00aa; animation:floatReverse 5s ease-in-out infinite 0.3s; }

        /* floating badges */
        .about-badge-1, .about-badge-2 {
          position: absolute;
          background: white; border-radius: 14px;
          padding: 11px 16px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.14);
          font-weight: 700; font-size: 0.82rem; color: #1a1a5e;
          display: flex; align-items: center; gap: 8px; z-index: 3;
        }
        .about-badge-1 { bottom: 14px; left: -16px; animation: float 3s ease-in-out infinite; }
        .about-badge-2 { top: 8px; right: -16px; animation: floatReverse 3.5s ease-in-out infinite; }

        /* experience pill */
        .collage-exp-pill {
          position: absolute; bottom: -44px; left: 50%; transform: translateX(-50%);
          background: linear-gradient(135deg, #1a1a5e, #2d2d8e);
          color: white; border-radius: 50px; padding: 10px 24px;
          font-size: 0.82rem; font-weight: 700; white-space: nowrap;
          box-shadow: 0 8px 24px rgba(26,26,94,0.4);
          display: flex; align-items: center; gap: 8px;
          animation: float 4s ease-in-out infinite; z-index: 3;
        }
        .about-text h2 { font-size: 2.2rem; font-weight: 800; color: #1a1a5e; margin-bottom: 16px; }
        .about-text h2 span { color: #ff6b35; }
        .about-text p { font-size: 0.95rem; color: #6c757d; line-height: 1.9; margin-bottom: 20px; }
        .about-info-list { list-style: none; padding: 0; margin: 20px 0; }
        .about-info-list li {
          display: flex; align-items: center; gap: 12px;
          padding: 10px 0; border-bottom: 1px solid #f0f0f0;
          font-size: 0.9rem; color: #343a40;
        }
        .about-info-list li span:first-child { font-weight: 600; color: #1a1a5e; min-width: 120px; }
        .skills-section { background: #f8f9fa; padding: 80px 0; }
        .skills-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 40px 60px; }
        /* client cards — same design as Home */
        .clients-cards-grid {
          display: grid; grid-template-columns: repeat(5, 1fr); gap: 20px;
        }
        .cl-card {
          background: white; border-radius: 14px;
          border: 1px solid #e9ecef; overflow: hidden;
          transition: all 0.4s cubic-bezier(0.175,0.885,0.32,1.275);
          cursor: default; position: relative;
        }
        .cl-card:hover {
          transform: translateY(-10px) scale(1.04);
          box-shadow: 0 20px 50px rgba(0,0,0,0.14);
          border-color: transparent;
        }
        .cl-bar { height: 5px; transition: height 0.3s ease; }
        .cl-card:hover .cl-bar { height: 8px; }
        .cl-body {
          padding: 20px 14px 18px;
          display: flex; flex-direction: column;
          align-items: center; text-align: center; gap: 7px;
        }
        .cl-icon {
          width: 56px; height: 56px; border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          font-size: 1.7rem; transition: all 0.4s ease;
          position: relative; overflow: hidden;
        }
        .cl-card:hover .cl-icon { transform: rotate(-6deg) scale(1.18); box-shadow: 0 8px 24px rgba(0,0,0,0.18); }
        .cl-icon::after {
          content: ''; position: absolute; top: -50%; left: -60%;
          width: 40%; height: 200%; background: rgba(255,255,255,0.38);
          transform: skewX(-20deg); transition: transform 0.55s ease;
        }
        .cl-card:hover .cl-icon::after { transform: skewX(-20deg) translateX(600%); }
        .cl-tag {
          display: inline-block; padding: 2px 10px;
          border-radius: 50px; font-size: 0.64rem;
          font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase;
        }
        .cl-name {
          font-size: 0.9rem; font-weight: 800; color: #1a1a5e;
          line-height: 1.25; white-space: pre-line;
          transition: color 0.3s ease;
        }
        .cl-card:hover .cl-name { color: #ff6b35; }
        .cl-sub { font-size: 0.72rem; color: #6c757d; }
        .cl-desc {
          font-size: 0.7rem; color: #adb5bd; opacity: 0;
          max-height: 0; overflow: hidden; transition: all 0.4s ease;
        }
        .cl-card:hover .cl-desc { opacity: 1; max-height: 36px; }
        @media (max-width: 1024px) { .clients-cards-grid { grid-template-columns: repeat(4,1fr); } }
        @media (max-width: 768px)  { .clients-cards-grid { grid-template-columns: repeat(3,1fr); } }
        @media (max-width: 480px)  { .clients-cards-grid { grid-template-columns: repeat(2,1fr); } }
        @media (max-width: 1024px) {
          .about-grid { grid-template-columns: 1fr; }
          .features-grid { grid-template-columns: 1fr; }
          .skills-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 768px) {
          .about-img-wrapper { display: none; }
        }
      `}</style>

      {/* Hero Banner */}
      <div className="page-hero">
        <div className="container">
          <AnimatedSection animation="reveal">
            <h1 className="page-hero-title">About <span>Us</span></h1>
            <p className="page-hero-subtitle">
              Learn about our story, mission, and the team behind Saanvi Innovations
            </p>
            <div className="breadcrumb">
              <Link to="/">Home</Link>
              <span>›</span>
              <span>About Us</span>
            </div>
          </AnimatedSection>
        </div>
      </div>

      {/* About Story */}
      <section className="section-padding">
        <div className="container">
          <div className="about-grid">
            <AnimatedSection animation="reveal-left">
              <div className="about-img-wrapper">

                {/* ── Slider frame ── */}
                <div className="about-slider-frame">
                  {aboutSlides.map((s, i) => (
                    <div key={i} className={`about-slide ${i === slideIdx ? 'active' : ''}`}>
                      <img src={s.img} alt={s.label} />
                      <div className="about-slide-overlay" style={{ background: s.overlay }} />
                      <div className="about-slide-label">{s.label}</div>
                    </div>
                  ))}

                  {/* arrows */}
                  <button
                    className="about-arrow about-arrow-l"
                    onClick={() => setSlideIdx(i => (i - 1 + aboutSlides.length) % aboutSlides.length)}
                    aria-label="Previous"
                  >‹</button>
                  <button
                    className="about-arrow about-arrow-r"
                    onClick={() => setSlideIdx(i => (i + 1) % aboutSlides.length)}
                    aria-label="Next"
                  >›</button>
                </div>

                {/* neon ring */}
                <div className="about-ring" />

                {/* dot indicators */}
                <div className="about-dots">
                  {aboutSlides.map((_, i) => (
                    <div
                      key={i}
                      className={`about-dot ${i === slideIdx ? 'active' : ''}`}
                      onClick={() => setSlideIdx(i)}
                    />
                  ))}
                </div>

                {/* floating particles */}
                <div className="collage-particle cp1" />
                <div className="collage-particle cp2" />
                <div className="collage-particle cp3" />
                <div className="collage-particle cp4" />

                {/* experience pill */}
                <div className="collage-exp-pill">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                  5+ Years of Digital Excellence
                </div>

                {/* floating badges */}
                <div className="about-badge-1">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#ff6b35" strokeWidth="2"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg>
                  Est. 2021
                </div>
                <div className="about-badge-2">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#1a1a5e" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                  5-Star Rated
                </div>

              </div>
            </AnimatedSection>

            <AnimatedSection animation="reveal-right">
              <div className="about-text">
                <span className="section-tag">Who We Are</span>
                <h2>We Are <span>Saanvi Innovations</span></h2>
                <p>
                  Saanvi Innovations is a private incorporated on 11 February 2021.
                  It is classified as non-government company and is registered at Registrar of Companies, Gwalior.
                  It is involved in Data processing and software development.
                </p>
                <p>
                  Our vision is to become a world leader in vertical IT solutions and services.
                  To achieve our vision, our team strives to deliver innovative, reliable and cost-effective
                  solutions and services to its clients in various technical aspects.
                </p>
                <ul className="about-info-list">
                  {/* <li><span>Founded</span> <span>11 February 2021</span></li> */}
                  {/* <li><span></span> <span>Private Limited Company</span></li> */}
                  <li><span>Registered</span> <span>ROC, Gwalior (M.P)</span></li>
                  <li><span>Activity</span> <span>Data Processing & Web Development</span></li>
                  <li><span>Location</span> <span>Gwalior, Madhya Pradesh, India</span></li>
                </ul>
                <Link to="/contact" className="btn btn-primary">Get In Touch →</Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Feature Boxes */}
      <section className="section-padding bg-light">
        <div className="container">
          <AnimatedSection className="section-header">
            <span className="section-tag">What Sets Us Apart</span>
            <h2 className="section-title">Why Choose <span>Us?</span></h2>
            <div className="section-divider"><span></span><span></span><span></span></div>
          </AnimatedSection>
          <div className="features-grid">
            {features.map((f, i) => (
              <AnimatedSection key={i} animation="reveal" delay={i * 120}>
                <div className="feature-card">
                  <div className="feature-icon">{f.icon}</div>
                  <div>
                    <h3>{f.title}</h3>
                    <p>{f.desc}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Skills */}
      <section className="skills-section">
        <div className="container">
          <AnimatedSection className="section-header">
            <span className="section-tag">Our Expertise</span>
            <h2 className="section-title">Technical <span>Skills</span></h2>
            <div className="section-divider"><span></span><span></span><span></span></div>
            <p className="section-subtitle">
              Our team brings expertise across a wide range of modern technologies and tools.
            </p>
          </AnimatedSection>
          <div className="skills-grid">
            {skills.map((s, i) => (
              <AnimatedSection key={i} animation="reveal" delay={i * 80}>
                <SkillBar skill={s.skill} percent={s.percent} color={s.color} delay={i * 100} />
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Clients */}
      <section className="section-padding">
        <div className="container">
          <AnimatedSection className="section-header">
            <span className="section-tag">Our Clients</span>
            <h2 className="section-title">Trusted by <span>Businesses</span></h2>
            <div className="section-divider"><span></span><span></span><span></span></div>
            <p className="section-subtitle" style={{ maxWidth: '820px' }}>
              A true partnership is a two-way street — ideas and information flow openly and regularly,
              based on a foundation of mutual trust and respect for one another's expertise.
            </p>
          </AnimatedSection>
          <div className="clients-cards-grid">
            {clientsData.map((c, i) => (
              <AnimatedSection key={i} animation="reveal-scale" delay={i * 70}>
                <div className="cl-card">
                  <div className="cl-bar" style={{ background: `linear-gradient(90deg, ${c.color}, ${c.accent})` }} />
                  <div className="cl-body">
                    <div className="cl-icon" style={{ background: `linear-gradient(135deg, ${c.color}22, ${c.color}44)` }}>
                      {c.icon}
                    </div>
                    <span className="cl-tag" style={{ background: `${c.color}18`, color: c.color }}>{c.tag}</span>
                    <div className="cl-name">{c.name}</div>
                    <div className="cl-sub">{c.sub}</div>
                    <div className="cl-desc">{c.desc}</div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── Office Gallery Section ── */}
      {/* <section style={{ padding:'96px 0', background:'#07071a' }}>
        <div className="container">
          <AnimatedSection className="section-header">
            <span className="section-tag" style={{ background:'rgba(232,96,58,0.12)', color:'#e8603a', border:'1px solid rgba(232,96,58,0.25)' }}>
              Our Office
            </span>
            <h2 className="section-title" style={{ color:'white' }}>
              Where the <span style={{ color:'#e8603a' }}>Magic Happens</span>
            </h2>
            <div className="section-divider"><span></span><span></span><span></span></div>
            <p className="section-subtitle" style={{ color:'rgba(255,255,255,0.55)' }}>
              A dedicated team of passionate developers, designers and marketers working together to deliver exceptional digital solutions from Gwalior, India.
            </p>
          </AnimatedSection> */}

          {/* ── Clean 2-row photo grid — no AnimatedSection wrappers inside grid ── */}
          {/* <AnimatedSection animation="reveal">
            <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gridTemplateRows:'280px 280px', gap:14 }}> */}

              {/* Big left — spans both rows */}
              {/* <div style={{ gridColumn:'1/2', gridRow:'1/3', borderRadius:18, overflow:'hidden', position:'relative', boxShadow:'0 12px 40px rgba(0,0,0,0.5)' }}>
                <img
                  src="/images/office/office-3.jpeg"
                  alt="Team collaboration at Saanvi Innovation"
                  style={{ width:'100%', height:'100%', objectFit:'cover', display:'block', transition:'transform 0.5s ease' }}
                  onMouseEnter={e=>e.target.style.transform='scale(1.04)'}
                  onMouseLeave={e=>e.target.style.transform='scale(1)'}
                  onError={e=>{ e.target.src='https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=900&q=80'; e.target.onerror=null; }}
                />
                <div style={{ position:'absolute', bottom:0, left:0, right:0, padding:'20px 24px', background:'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 100%)' }}>
                  <div style={{ color:'#e8603a', fontSize:'0.68rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:4 }}>Team</div>
                  <div style={{ color:'white', fontWeight:700, fontSize:'0.95rem' }}>Collaboration &amp; Brainstorming</div>
                </div>
              </div> */}

              {/* Top middle */}
              {/* <div style={{ gridColumn:'2/3', gridRow:'1/2', borderRadius:18, overflow:'hidden', position:'relative', boxShadow:'0 12px 40px rgba(0,0,0,0.5)' }}>
                <img
                  src="/images/office/office-1.jpeg"
                  alt="Design team at workstations"
                  style={{ width:'100%', height:'100%', objectFit:'cover', display:'block', transition:'transform 0.5s ease' }}
                  onMouseEnter={e=>e.target.style.transform='scale(1.04)'}
                  onMouseLeave={e=>e.target.style.transform='scale(1)'}
                  onError={e=>{ e.target.src='https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=600&q=80'; e.target.onerror=null; }}
                />
                <div style={{ position:'absolute', bottom:0, left:0, right:0, padding:'14px 18px', background:'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)' }}>
                  <div style={{ color:'white', fontWeight:700, fontSize:'0.85rem' }}>Design Team</div>
                </div>
              </div> */}

              {/* Top right */}
              {/* <div style={{ gridColumn:'3/4', gridRow:'1/2', borderRadius:18, overflow:'hidden', position:'relative', boxShadow:'0 12px 40px rgba(0,0,0,0.5)' }}>
                <img
                  src="/images/office/office-2.jpeg"
                  alt="Development team working"
                  style={{ width:'100%', height:'100%', objectFit:'cover', display:'block', transition:'transform 0.5s ease' }}
                  onMouseEnter={e=>e.target.style.transform='scale(1.04)'}
                  onMouseLeave={e=>e.target.style.transform='scale(1)'}
                  onError={e=>{ e.target.src='https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&q=80'; e.target.onerror=null; }}
                />
                <div style={{ position:'absolute', bottom:0, left:0, right:0, padding:'14px 18px', background:'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)' }}>
                  <div style={{ color:'white', fontWeight:700, fontSize:'0.85rem' }}>Dev Workspace</div>
                </div>
              </div> */}

              {/* Bottom middle */}
              {/* <div style={{ gridColumn:'2/3', gridRow:'2/3', borderRadius:18, overflow:'hidden', position:'relative', boxShadow:'0 12px 40px rgba(0,0,0,0.5)' }}>
                <img
                  src="/images/office/office-4.jpeg"
                  alt="Full office view"
                  style={{ width:'100%', height:'100%', objectFit:'cover', display:'block', transition:'transform 0.5s ease' }}
                  onMouseEnter={e=>e.target.style.transform='scale(1.04)'}
                  onMouseLeave={e=>e.target.style.transform='scale(1)'}
                  onError={e=>{ e.target.src='https://images.unsplash.com/photo-1531403009284-440f080d1e12?w=600&q=80'; e.target.onerror=null; }}
                />
                <div style={{ position:'absolute', bottom:0, left:0, right:0, padding:'14px 18px', background:'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)' }}>
                  <div style={{ color:'white', fontWeight:700, fontSize:'0.85rem' }}></div>
                </div>
              </div> */}

              {/* Bottom right */}
              {/* <div style={{ gridColumn:'3/4', gridRow:'2/3', borderRadius:18, overflow:'hidden', position:'relative', boxShadow:'0 12px 40px rgba(0,0,0,0.5)' }}>
                <img
                  src="/images/office/office-5.jpeg"
                  alt="Engineers working on projects"
                  style={{ width:'100%', height:'100%', objectFit:'cover', display:'block', transition:'transform 0.5s ease' }}
                  onMouseEnter={e=>e.target.style.transform='scale(1.04)'}
                  onMouseLeave={e=>e.target.style.transform='scale(1)'}
                  onError={e=>{ e.target.src='https://images.unsplash.com/photo-1560472355-536de3962603?w=600&q=80'; e.target.onerror=null; }}
                />
                <div style={{ position:'absolute', bottom:0, left:0, right:0, padding:'14px 18px', background:'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)' }}>
                  <div style={{ color:'white', fontWeight:700, fontSize:'0.85rem' }}>Innovation Lab</div>
                </div>
              </div>

            </div>
          </AnimatedSection> */}

          {/* Stats strip */}
          {/* <AnimatedSection>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, marginTop:20 }}>
              {[
                { num:'15+', label:'Team Members',      icon:<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#e8603a" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
                { num:'2021', label:'Est. Gwalior, M.P.',icon:<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#e8603a" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg> },
                { num:'200+', label:'Projects Delivered', icon:<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#e8603a" strokeWidth="2"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg> },
              ].map((s,i) => (
                <div key={i} style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:14, padding:'22px 24px', display:'flex', alignItems:'center', gap:14 }}>
                  <div style={{ width:44, height:44, borderRadius:12, background:'rgba(232,96,58,0.1)', border:'1px solid rgba(232,96,58,0.2)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{s.icon}</div>
                  <div>
                    <div style={{ fontSize:'1.75rem', fontWeight:800, color:'white', letterSpacing:'-0.04em', lineHeight:1 }}>{s.num}</div>
                    <div style={{ fontSize:'0.72rem', color:'rgba(255,255,255,0.4)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', marginTop:5 }}>{s.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section> */}
    </div>
  );
};

export default About;
